import { IObjectTranslatable, HtmlColorCodeTranslator, RelationalActionTranslator } from ".";
import { ITypeDefinable, BeanTypes, IGraphItemBean, IGraphSubItemBean, IGraphValueItemBean } from "../beans/items";
import { IItem, GraphItem, GraphSubItem, GraphValueItem } from "../../../../domain/document/items";
import { Point, Size, Rectangle } from "../../../../domain/drawing";
import { HeightRangePair, LimitPair, DateFormatter} from "../../../../domain/system";

export class GraphItemTranslator  implements IObjectTranslatable {
  toItem(item: ITypeDefinable, formatId: number, groupId: number, documentId: number, executionMode: string): IItem {
    const bean = item as IGraphItemBean;
    const result = new GraphItem();
    result.privateId = (bean as any).privateId; // フロントエンド独自パラメータ.
    result.uniqueKey = bean.uniqueKey;
    result.id = bean.id;
    result.sectionObjectUid = bean.sectionObjectUid;
    result.name = bean.name;
    result.isEditable = true; // TODO:特に問題なければプロパティを削除する
    result.isAdditional = !!bean.isAdditional;
    result.isDeleted = !!bean.isDeleted;
    result.isVisible = true;
    result.typeDetail = bean.typeDetail;
    result.viewTitle = bean.viewTitle;
    result.printBehavior.shouldPrint = !!bean.printVisible;
    result.printBehavior.shouldPreview = !!bean.previewVisible;
    result.geometry.rectangle = new Rectangle(new Point(bean.x, bean.y), new Size(bean.width, bean.height));
    result.limitOfValue = new LimitPair<number>(bean.graphMinValue, bean.graphMaxValue);
    result.rangeOfValue = new HeightRangePair<number>(bean.graphLowValue, bean.graphHighValue);
    result.muscularStrengthInputKind = bean.muscularStrengthInputType;
    result.muscularStrengthValueKind = bean.muscularStrengthValueType;
    result.graphSubItems = this.toGraphSubItems(bean.graphSubItems, bean.graphDateText, bean.graphValueText);
    result.scaleKind = bean.graphScaleType;
    result.shouldDrawBorder = bean.graphBorder;
    result.horizontalDivideCount = bean.horizontalGrid;
    result.referenceIds = this.extractReferenceIds(bean.graphSubItems);
    result.actions = RelationalActionTranslator.toItems(bean.relationalItems);
    return result;
  }

  private extractReferenceIds(subItems: IGraphSubItemBean[]): string[] {
    if (!subItems) { return []; }
    const ids: string[] = [];
    for (const subItem of subItems) {
      if (!subItem.graphValues) { continue; }
      for (const valueItem of subItem.graphValues) {
        if (valueItem.dateObjectId && ids.indexOf(valueItem.dateObjectId) < 0) {
          ids.push(valueItem.dateObjectId);
        }
        if (valueItem.valueObjectId && ids.indexOf(valueItem.valueObjectId) < 0) {
          ids.push(valueItem.valueObjectId);
        }
      }
    }
    return ids.sort();
  }

  /**
   * 各項目の値は並び順しか情報が無いため、上から順に値を適用させる
   */
  toGraphSubItems(beans: IGraphSubItemBean[], graphDateText: string, graphValueText: string): GraphSubItem[] {
    if (!beans) { return []; }
    const items: GraphSubItem[] = [];
    const dateTexts = graphDateText.split(",");
    const valueTexts = graphValueText.split(",");
    let index = 0;
    for (const bean of beans) {
      const graphValueItems: GraphValueItem[] = [];
      if (bean.graphValues) {
        for (const graphValueBean of bean.graphValues) {
          const dateText = dateTexts[index] ? dateTexts[index].trim() : null;
          const valueText = valueTexts[index] ? valueTexts[index].trim() : null;
          graphValueItems.push(this.toGraphValueItem(graphValueBean, dateText, valueText));
          index++;
        }
      }
      const item = this.toGraphSubItem(bean);
      item.graphValues = graphValueItems;
      items.push(item);
    }
    return items;
  }

  toGraphSubItem(bean: IGraphSubItemBean): GraphSubItem {
    if (!bean) { return null; }
    const item = new GraphSubItem();
    item.name = bean.name;
    item.rangeOfValue = new HeightRangePair<number>(bean.lowValue, bean.highValue);
    item.backColor = HtmlColorCodeTranslator.toColor(bean.backColor);
    item.foreColor = HtmlColorCodeTranslator.toColor(bean.foreColor);
    // graphValuesはあとでセットする
    return item;
  }

  toGraphValueItem(bean: IGraphValueItemBean, date: string, value: string): GraphValueItem {
    const item = new GraphValueItem();
    item.dateObjectId = bean.dateObjectId;
    item.date = this.toDate(date);
    item.valueObjectId = bean.valueObjectId;
    item.value = value ? Number(value) : null;
    return item;
  }

  private toDate(text: string): Date {
    if (!text) { return null; }
    const value = (text + "00000000000000").substr(0, 14);
    return value === "00000000000000"
      ? null
      : new Date(
        Number(value.substr(0, 4)),
        Number(value.substr(4, 2)) - 1,
        Number(value.substr(6, 2)),
        Number(value.substr(8, 2)),
        Number(value.substr(10, 2)),
        Number(value.substr(12, 2))
      );
  }


  toBean(item: IItem): IGraphItemBean {
    const target = item as GraphItem;
    return {
      type: BeanTypes.Graph,
      uniqueKey: target.uniqueKey,
      id: target.id,
      sectionObjectUid: target.sectionObjectUid,
      name: target.name,
      isVisible: true,  // 返却不要だがI/Fに入っているのでコンパイルエラー回避で入れている
      printVisible: target.printBehavior.shouldPrint,
      previewVisible: target.printBehavior.shouldPreview,
      x: target.geometry.rectangle.x,
      y: target.geometry.rectangle.y,
      width: target.geometry.rectangle.width,
      height: target.geometry.rectangle.height,
      isAdditional: target.isAdditional,
      isDeleted: target.isDeleted,
      graphMinValue: target.limitOfValue.min,
      graphMaxValue: target.limitOfValue.max,
      graphLowValue: target.rangeOfValue.low,
      graphHighValue: target.rangeOfValue.high,
      muscularStrengthInputType: target.muscularStrengthInputKind,
      muscularStrengthValueType: target.muscularStrengthValueKind,
      graphSubItems: null, // 返却不要だがI/Fに入っているのでコンパイルエラー回避で入れている
      graphScaleType: target.scaleKind,
      graphBorder: target.shouldDrawBorder,
      horizontalGrid: target.horizontalDivideCount,
      graphDateText: this.toGraphDateText(target.graphSubItems),
      graphValueText: this.toGraphValueText(target.graphSubItems),
      typeDetail: target.typeDetail,
      viewTitle: target.viewTitle,
      relationalItems: null,
    };
  }
  private toGraphDateText(subItems: GraphSubItem[]): string {
    const texts: string[] = [];
    for (const subItem of subItems) {
      for (const valueItem of subItem.graphValues) {
        const text = valueItem.date ? DateFormatter.format(valueItem.date, "YYYYMMDDHHmmss") : "00000000000000";
        texts.push(text);
      }
    }
    return texts.join(",");
  }

  private toGraphValueText(subItems: GraphSubItem[]): string {
    const texts: string[] = [];
    for (const subItem of subItems) {
      for (const valueItem of subItem.graphValues) {
        const text = (valueItem.value != null && !isNaN(valueItem.value) && isFinite(valueItem.value)) ? valueItem.value.toString() : "";
        texts.push(text);
      }
    }
    return texts.join(",");
  }
}
